<template>
  <div class="dashboard-view">
    <div class="card border-0">
      <div class="card-body border-0 pt-0 pb-3 pl-0 pr-0" style="background-color: #E3E8EE">
        <div class="general-info-wrapper">
          <amount-viewer :title="$t('Total Items')" text-class="text-info" key="items-general-info"
                         v-animate-fade="0.2" :clickable="true" @click="onTotalItemsClick"
                         :amount="generalInfoData.items" :icon="'boxes'" :loading="generalInfoLoader"/>
          <amount-viewer :title="$t('Reserved Items')" text-class="text-info" v-animate-fade="0.4"
                         key="reserved_items-general-info" :clickable="true" @click="onReservedItemsClick"
                         :amount="generalInfoData.reserved_items" :icon="'boxes'" :loading="generalInfoLoader"/>
          <amount-viewer :title="$t('Sold Items')" text-class="text-success"
                         v-animate-fade="0.6" :clickable="true" @click="onSoldItemsClick"
                         key="sold_items-general-info"
                         :icon="'boxes'" :amount="generalInfoData.sold_items" :loading="generalInfoLoader"/>
          <amount-viewer :title="$t('Unsold Items')" text-class="text-danger" v-animate-fade="0.8"
                         key="unsold_items-general-info" :clickable="true" @click="onUnsoldItemsClick"
                         :icon="'boxes'" :amount="generalInfoData.unsold_items" :loading="generalInfoLoader"/>
          <amount-viewer :title="$t('Estimates')" :icon="'money-bill'"
                         v-animate-fade="1" key="low_estimate-general-info"
                         :amount="computedEstimates" :loading="generalInfoLoader"/>
          <amount-viewer :title="$t('Reserve')" :icon="'money-bill'" key="reserve-general-info"
                         v-animate-fade="1.2"
                         :amount="$options.filters.asCurrency(generalInfoData.reserve) || $options.filters.asCurrency(0)"
                         :loading="generalInfoLoader"/>
        </div>
        <div class="main-wrapper">
          <div class="card receipt-card" v-animate-fade="0.2">
            <div class="card-header d-flex align-items-center">
              <h5 class="d-inline-block mb-0">
                {{ $t('Receipt Information') }}
              </h5>
              <b-button v-permission="'change_receipt'" class="ml-auto p-0" variant="link"
                        @click="onEditClick(receipt)">
                <font-awesome-icon :icon="'pen'" style="font-size: .9em"></font-awesome-icon>
                {{ $t('Edit') }}
              </b-button>
            </div>
            <div class="card-body">
              <receipt-info-table :receipt="receipt"/>
            </div>
          </div>
          <div class="card customer-card" v-animate-fade="0.4">
            <div class="card-header d-flex align-items-center">
              <h5 class="d-inline-block mb-0">
                {{ $t('Vendor Information') }}
              </h5>
              <b-button class="ml-auto p-0" variant="link" v-permission="'change_customer'"
                        @click="onEditCustomerClick({source: SOURCE_RECEIPT_DASHBOARD, object: receipt.customer})">
                <font-awesome-icon :icon="'pen'" style="font-size: .9em"></font-awesome-icon>
                {{ $t('Edit') }}
              </b-button>
            </div>
            <div class="card-body">
              <customer-info-table
                :exclude-fields="['email2','phone2','tags','acquisition_source']"
                :customer="receipt.customer"/>
            </div>
          </div>
          <div class="card items-by-category-card animated" v-animate-fade="0.6">
            <div class="card-header">
              <h5 class="mb-0">{{ $t('Items by Category') }}</h5>
            </div>
            <no-data-available
              v-if="itemsByCategoryData.labels && !itemsByCategoryData.labels.length && !itemsByCategoryLoader"
              :height="5"/>
            <div v-else class="card-body d-flex align-items-center justify-content-center">
              <spinner height="15" :show="itemsByCategoryLoader" :with-text="false"/>
              <e-pie-chart v-if="!itemsByCategoryLoader && itemsByCategoryData && itemsByCategoryData.length > 0"
                           :chart-data="itemsByCategoryData"/>
              <no-data-available v-if="itemsByCategoryData && itemsByCategoryData.length === 0"/>
            </div>
          </div>
          <div class="card animated miscellaneous-card" v-animate-fade="0.8" v-if="false">
            <div class="card-header">
              <h5 class="mb-0">{{ $t('Miscellaneous Charges') }}</h5>
            </div>
            <no-data-available v-if="!receipt.miscellaneous_items.length" :height="5"/>
            <div v-else class="card-body miscellaneous-table">
              <table class="table table-sm">
                <tr>
                  <th class="border-top-0">{{ $t('Title') }}</th>
                  <th class="border-top-0">{{ $t('Comment') }}</th>
                  <th class="border-top-0">{{ $t('Amount') }}</th>
                  <th class="border-top-0">{{ $t('Item') }}</th>
                  <th class="border-top-0">{{ $t('Taxable') }}</th>
                  <th class="border-top-0">{{ $t('Settled') }}</th>
                </tr>
                <tr v-for="(item, index) in receipt.miscellaneous_items" :key="`misc-item-row-${index}`">
                  <td>{{ item.description }}</td>
                  <td>{{ item.comment }}</td>
                  <td>{{ calculateMiscItemAmount(item) | asCurrency }}</td>
                  <td>
                    <template v-if="item.inventory">
                      <router-link v-if="$hasPermission('view_inventoryitem')"
                                   :to="{ name: 'inventory.view', params: { id: item.inventory_uuid }}">
                        {{ item.inventory.item_no }}
                      </router-link>
                      <span v-else>
                        {{ item.inventory.item_no }}
                      </span>
                    </template>
                  </td>
                  <td>
                    <span v-if="item.taxable" class="badge bg-success text-white">{{ $t('Yes') }}</span>
                    <span v-else class="badge bg-danger text-white">{{ $t('No') }}</span>
                  </td>
                  <td>
                    <span v-if="item.settlement_uuid" class="badge bg-success text-white">{{ $t('Yes') }}</span>
                    <span v-else class="badge bg-danger text-white">{{ $t('No') }}</span>
                  </td>
                </tr>
              </table>
            </div>
          </div>
          <div class="card terms-card animated" v-animate-fade="1">
            <div class="card-header">
              <h5 class="mb-0">{{ $t('Terms') }}</h5>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-6 border-right">
                  <commissions-view-table v-if="receipt.commissions && receipt.commissions.length > 0"
                                          :commissions="receipt.commissions"/>
                </div>
                <div class="col-md-6">
                  <table class="info-table table mb-0 table-sm">
                    <tr>
                      <td class="border-top-0 font-weight-bold">{{ $t('Minimum commission') }}</td>
                      <td class="border-top-0">{{ receipt.minimum_commission | asCurrency }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">{{ $t('L/D Warranty') }}</td>
                      <td>{{ receipt.insurance_rate | asPercentage }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">{{ $t('Entry Fee') }}</td>
                      <td>{{ receipt.lot_handling_charge | asCurrency }}</td>
                    </tr>
                  </table>
                </div>
              </div>
            </div>
          </div>
          <div class="card charges-card animated" v-animate-fade="1">
            <div class="card-header">
              <h5 class="mb-0">{{ $t('Vendor Charges') }}</h5>
            </div>
            <div class="card-body">
              <div class="row" v-if="vendorCharges && vendorCharges.length > 0">
                <div class="col-sm-6 col-md-6 col-lg-6 pb-3" v-for="charge in vendorCharges" :key="charge.uuid">
                  <b-form-checkbox :disabled="!$hasPermission('change_receipt')" v-model="charge.checked"
                                   @change="onVendorChangeCheckboxChange" switch>
                    <span class="d-inline-block mb-0">
                      {{ charge.name }}
                    </span>
                  </b-form-checkbox>
                </div>
              </div>
              <no-data-available v-else height="8"/>
            </div>
          </div>
          <div class="card relations-card" v-if="enableNlRelations">
            <div class="card-header">
              <h5 class="mb-0">{{ $t("Relations") }}</h5>
            </div>
            <div class="card-body">
              <spinner :with-text="false" height="5" show v-if="relations.loader"/>
              <table v-else class="table table-sm mb-0">
                <tr>
                  <th class="border-top-0"></th>
                  <th class="border-top-0">{{ $t('Customer') }}</th>
                  <th class="border-top-0">{{ $t('Confirmations') }}</th>
                  <th class="border-top-0">{{ $t('Suggestions') }}</th>
                  <th class="border-top-0">{{ $t('Settlements') }}</th>
                  <th class="border-top-0">{{ $t('Remove from CC') }}</th>
                  <th class="border-top-0">{{ $t('Reference Number') }}</th>
                  <th class="border-top-0">{{ $t('Intermediary Commission Rate') }}</th>
                  <th class="border-top-0">{{ $t('Including VAT') }}</th>
                  <th class="border-top-0"></th>
                </tr>
                <tr class="hover">
                  <th>{{ $t("Notary") }}</th>
                  <td>
                    <template v-if="relations.notary && relations.notary.related_customer">
                      <router-link v-if="relations.notary.related_customer && $hasPermission('view_customer')"
                                   :to="{ name: 'customers.dashboard', params: { id: relations.notary.related_customer.uuid }}">
                        {{ relations.notary.related_customer.display_name }}
                      </router-link>
                    </template>
                  </td>
                  <td>
                    <template v-if="relations.notary">
                      <span class="badge badge-success" v-if="relations.notary.confirmations">
                        {{ $t('Yes') }}
                      </span>
                      <span class="badge badge-danger" v-else>{{ $t('No') }}</span>
                    </template>
                  </td>
                  <td>
                    <template v-if="relations.notary">
                      <span class="badge badge-success" v-if="relations.notary.suggestions">
                        {{ $t('Yes') }}
                      </span>
                      <span class="badge badge-danger" v-else>{{ $t('No') }}</span>
                    </template>
                  </td>
                  <td>
                    <template v-if="relations.notary">
                      <span class="badge badge-success" v-if="relations.notary.settlements">
                        {{ $t('Yes') }}
                      </span>
                      <span class="badge badge-danger" v-else>{{ $t('No') }}</span>
                    </template>
                  </td>
                  <td>
                    <template v-if="relations.notary">
                      <span class="badge badge-success" v-if="relations.notary.cc">
                        {{ $t('Yes') }}
                      </span>
                      <span class="badge badge-danger" v-else>{{ $t('No') }}</span>
                    </template>
                  </td>
                  <td>
                    <template v-if="relations.notary">
                      {{ relations.notary.reference_number }}
                    </template>
                  </td>
                  <td></td>
                  <td></td>
                  <td>
                    <b-button v-permission="'change_customer'" variant="primary" size="sm"
                              @click="onRelationEditClick('notary')">
                      <font-awesome-icon :icon="'pen'"></font-awesome-icon>
                    </b-button>
                    <b-button v-permission="'change_customer'" variant="danger" size="sm"
                              v-if="relations && relations.notary"
                              @click="onRelationDeleteClick('notary')" class="ml-2">
                      <font-awesome-icon :icon="'trash'"></font-awesome-icon>
                    </b-button>
                  </td>
                </tr>
                <tr>
                  <th>{{ $t("Intermediary") }}</th>
                  <td>
                    <template v-if="relations.intermediary && relations.intermediary.related_customer">
                      <router-link v-if="relations.intermediary.related_customer && $hasPermission('view_customer')"
                                   :to="{ name: 'customers.dashboard', params: { id: relations.intermediary.related_customer.uuid }}">
                        {{ relations.intermediary.related_customer.display_name }}
                      </router-link>
                    </template>
                  </td>
                  <td>
                    <template v-if="relations.intermediary">
                      <span class="badge badge-success" v-if="relations.intermediary.confirmations">
                        {{ $t('Yes') }}
                      </span>
                      <span class="badge badge-danger" v-else>{{ $t('No') }}</span>
                    </template>
                  </td>
                  <td>
                    <template v-if="relations.intermediary">
                      <span class="badge badge-success" v-if="relations.intermediary.suggestions">
                        {{ $t('Yes') }}
                      </span>
                      <span class="badge badge-danger" v-else>{{ $t('No') }}</span>
                    </template>
                  </td>
                  <td>
                    <template v-if="relations.intermediary">
                      <span class="badge badge-success" v-if="relations.intermediary.settlements">
                        {{ $t('Yes') }}
                      </span>
                      <span class="badge badge-danger" v-else>{{ $t('No') }}</span>
                    </template>
                  </td>
                  <td>
                    <template v-if="relations.intermediary">
                      <span class="badge badge-success" v-if="relations.intermediary.cc">
                        {{ $t('Yes') }}
                      </span>
                      <span class="badge badge-danger" v-else>{{ $t('No') }}</span>
                    </template>
                  </td>
                  <td>
                    <template v-if="relations.intermediary">
                      {{ relations.intermediary.reference_number }}
                    </template>
                  </td>
                  <td>
                    <template v-if="relations.intermediary">
                      {{ getIntermediaryRate(relations.intermediary.intermediary_commission_rate) | asPercentage }}
                    </template>
                  </td>
                  <td>
                    <template v-if="relations.intermediary">
                      <span class="badge badge-success" v-if="relations.intermediary.including_vat">
                        {{ $t('Yes') }}
                      </span>
                      <span class="badge badge-danger" v-else>{{ $t('No') }}</span>
                    </template>
                  </td>
                  <td>
                    <b-button v-permission="'change_customer'" variant="primary" size="sm"
                              @click="onRelationEditClick('intermediary')">
                      <font-awesome-icon :icon="'pen'"></font-awesome-icon>
                    </b-button>
                    <b-button v-permission="'change_customer'" variant="danger" size="sm"
                              v-if="relations && relations.intermediary"
                              @click="onRelationDeleteClick('intermediary')" class="ml-2">
                      <font-awesome-icon :icon="'trash'"></font-awesome-icon>
                    </b-button>
                  </td>
                </tr>
                <tr>
                  <th>{{ $t("Contact Person") }}</th>
                  <td>
                    <template v-if="relations.contactPerson && relations.contactPerson.related_customer">
                      <router-link v-if="relations.contactPerson.related_customer && $hasPermission('view_customer')"
                                   :to="{ name: 'customers.dashboard', params: { id: relations.contactPerson.related_customer.uuid }}">
                        {{ relations.contactPerson.related_customer.display_name }}
                      </router-link>
                    </template>
                  </td>
                  <td>
                    <template v-if="relations.contactPerson">
                      <span class="badge badge-success" v-if="relations.contactPerson.confirmations">
                        {{ $t('Yes') }}
                      </span>
                      <span class="badge badge-danger" v-else>{{ $t('No') }}</span>
                    </template>
                  </td>
                  <td>
                    <template v-if="relations.contactPerson">
                      <span class="badge badge-success" v-if="relations.contactPerson.suggestions">
                        {{ $t('Yes') }}
                      </span>
                      <span class="badge badge-danger" v-else>{{ $t('No') }}</span>
                    </template>
                  </td>
                  <td>
                    <template v-if="relations.contactPerson">
                      <span class="badge badge-success" v-if="relations.contactPerson.settlements">
                        {{ $t('Yes') }}
                      </span>
                      <span class="badge badge-danger" v-else>{{ $t('No') }}</span>
                    </template>
                  </td>
                  <td>
                    <template v-if="relations.contactPerson">
                      <span class="badge badge-success" v-if="relations.contactPerson.cc">
                        {{ $t('Yes') }}
                      </span>
                      <span class="badge badge-danger" v-else>{{ $t('No') }}</span>
                    </template>
                  </td>
                  <td>
                    <template v-if="relations.contactPerson">
                      {{ relations.contactPerson.reference_number }}
                    </template>
                  </td>
                  <td></td>
                  <td></td>
                  <td style="min-width: 80px">
                    <b-button v-permission="'change_customer'" variant="primary" size="sm"
                              @click="onRelationEditClick('contact_person')">
                      <font-awesome-icon :icon="'pen'"></font-awesome-icon>
                    </b-button>
                    <b-button v-permission="'change_customer'" variant="danger" size="sm"
                              v-if="relations && relations.contactPerson"
                              @click="onRelationDeleteClick('contact_person')" class="ml-2">
                      <font-awesome-icon :icon="'trash'"></font-awesome-icon>
                    </b-button>
                  </td>
                </tr>
              </table>
            </div>
          </div>
          <div class="card relations-card" v-if="enableNlRelations">
            <div class="card-header d-flex align-items-center">
              <h5 class="d-inline-block mb-0">
                {{ $t('Persons of Interest') }}
              </h5>
              <b-button v-permission="'change_receipt'" class="ml-auto p-0" variant="link"
                        @click="onAddNewPersonOfInterestClick()">
                <font-awesome-icon :icon="'plus'" style="font-size: .9em"></font-awesome-icon>
                {{ $t('Add New') }}
              </b-button>
            </div>
            <div class="card-body">
              <spinner :with-text="false" height="5" show v-if="relations.loader"/>
              <table v-else-if="relations && relations.personsOfInterest && relations.personsOfInterest.length > 0"
                     class="table table-sm mb-0">
                <tr>
                  <th class="border-top-0">{{ $t('Customer') }}</th>
                  <th class="border-top-0">{{ $t('Confirmations') }}</th>
                  <th class="border-top-0">{{ $t('Suggestions') }}</th>
                  <th class="border-top-0">{{ $t('Settlements') }}</th>
                  <th class="border-top-0">{{ $t('Remove from CC') }}</th>
                  <th class="border-top-0">{{ $t('Intermediary Commission Rate') }}</th>
                  <th class="border-top-0"></th>
                </tr>
                <tr v-for="personOfInterest in relations.personsOfInterest" :key="personOfInterest.uuid">
                  <td>
                    <template v-if="personOfInterest && personOfInterest.related_customer">
                      <router-link v-if="$hasPermission('view_customer')"
                                   :to="{ name: 'customers.dashboard', params: { id: personOfInterest.related_customer.uuid }}">
                        {{ personOfInterest.related_customer.display_name }}
                      </router-link>
                    </template>
                  </td>
                  <td>
                    <template v-if="personOfInterest">
                      <span class="badge badge-success" v-if="personOfInterest.confirmations">
                        {{ $t('Yes') }}
                      </span>
                      <span class="badge badge-danger" v-else>{{ $t('No') }}</span>
                    </template>
                  </td>
                  <td>
                    <template v-if="personOfInterest">
                      <span class="badge badge-success" v-if="personOfInterest.suggestions">
                        {{ $t('Yes') }}
                      </span>
                      <span class="badge badge-danger" v-else>{{ $t('No') }}</span>
                    </template>
                  </td>
                  <td>
                    <template v-if="personOfInterest">
                      <span class="badge badge-success" v-if="personOfInterest.settlements">
                        {{ $t('Yes') }}
                      </span>
                      <span class="badge badge-danger" v-else>{{ $t('No') }}</span>
                    </template>
                  </td>
                  <td>
                    <template v-if="personOfInterest">
                      <span class="badge badge-success" v-if="personOfInterest.cc">
                        {{ $t('Yes') }}
                      </span>
                      <span class="badge badge-danger" v-else>{{ $t('No') }}</span>
                    </template>
                  </td>
                  <td></td>
                  <td style="min-width: 80px">
                    <b-button v-permission="'change_customer'" variant="primary" size="sm"
                              @click="onEditPersonOfInterestClick(personOfInterest)">
                      <font-awesome-icon :icon="'pen'"></font-awesome-icon>
                    </b-button>
                    <b-button v-permission="'change_customer'" variant="danger" size="sm"
                              @click="onRelationDeleteClick(null, personOfInterest)" class="ml-2">
                      <font-awesome-icon :icon="'trash'"></font-awesome-icon>
                    </b-button>
                  </td>
                </tr>
              </table>
              <no-data-available v-else height="5"/>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

  import {createNamespacedHelpers} from "vuex";
  import {SOURCE_RECEIPT_DASHBOARD} from "../../../constants";
  import ReceiptInfoTable from "./ReceiptInfoTable";
  import CustomerInfoTable from "../../../customer/view/dashboard/CustomerInfoTable";
  import AmountViewer from "../../../../core/components/common/AmountViewer";
  import Spinner from "../../../../core/components/common/Spinner";
  import CommissionsViewTable from "@/modules/receipt/view/dashboard/CommissionsViewTable";
  import NoDataAvailable from "../../../../core/components/common/NoDataAvailable";
  import EPieChart from "@/core/components/charts/EPieChart";
  import BranchService from "@/modules/setup/branch/BranchService";
  import _ from "lodash";
  import ReceiptService from "@/modules/receipt/ReceiptService";
  import RelationsService from "@/modules/relations/RelationsService";

  const {mapActions, mapState} = createNamespacedHelpers('receipts');
  const {mapActions: customerMapActions} = createNamespacedHelpers('customer');
  const {mapActions: customerViewMapActions} = createNamespacedHelpers('customer/customerView');
  const {mapActions: inventoryMapActions} = createNamespacedHelpers('inventory')
  const {mapGetters: globalMapGetters, mapState: globalMapState} = createNamespacedHelpers('global');

  /**
   * @author Saiat Kalbiev <kalbievich11@gmail.com>
   */
  export default {
    name: "DashboardView",
    components: {
      EPieChart,
      CommissionsViewTable,
      Spinner,
      AmountViewer,
      CustomerInfoTable,
      ReceiptInfoTable,
      NoDataAvailable
    },
    data: () => {
      return {
        SOURCE_RECEIPT_DASHBOARD: SOURCE_RECEIPT_DASHBOARD,
        vendorCharges: [],
        hiddenVendorCharges: []
      };
    },
    computed: {
      ...globalMapGetters(['isIntermediaryCustomerTypesEnabled']),
      ...globalMapState(['currentBranch', 'inclusiveTiersEnabled', 'inclusiveTaxForFinancialObjects', 'enableNlRelations']),
      ...mapState({
        receipt: state => state.view.currentReceipt,
        generalInfoData: state => state.view.dashboardData.generalInfo.data,
        generalInfoLoader: state => state.view.dashboardData.generalInfo.loader,
        itemsByCategoryLoader: state => state.view.dashboardData.itemsByCategory.loader,
        itemsByCategoryData: state => state.view.dashboardData.itemsByCategory.data,
        relations: state => state.view.dashboardData.relations,
      }),
      computedEstimates: function () {
        if (this.generalInfoData.low_estimate || this.generalInfoData.high_estimate) {
          return this.$options.filters.asCurrency(this.generalInfoData.low_estimate) + ' - ' + this.$options.filters.asCurrency(this.generalInfoData.high_estimate)
        }
        return this.$options.filters.asCurrency(0);
      },

    },
    methods: {
      ...customerMapActions({
        showCustomerModal: 'showModal',
        getCustomerModalObject: 'getModalObject',
      }),
      ...customerViewMapActions({
        showRelationModal: 'showRelationModal',
      }),
      ...mapActions(['showModal', 'getDashboardData', 'getModalObject', 'getDashboardRelations']),
      ...inventoryMapActions({
        updateTableSourceInventory: 'updateTableSource',
        updateSearchModelInventory: 'updateSearchModel',
        resetSearchModelInventory: 'resetSearchModel',
      }),
      calculateMiscItemAmount(item) {
        if (item.taxable && this.inclusiveTaxForFinancialObjects) {
          let cfc = this.currentBranch.tax_rate / 100 + 1
          return Math.round(parseFloat(item.amount * cfc))
        }
        return item.amount
      },
      onEditClick(item) {
        this.showModal({
          object: this.receipt,
          source: SOURCE_RECEIPT_DASHBOARD
        });
        this.getModalObject({uuid: item.uuid, source: SOURCE_RECEIPT_DASHBOARD});
      },
      onEditCustomerClick() {
        this.showCustomerModal();
        this.getCustomerModalObject({uuid: this.receipt.customer.uuid, source: SOURCE_RECEIPT_DASHBOARD});
      },
      goToInventoryWithCurrentReceiptAndStatusOf(status) {
        this.updateTableSourceInventory({source: "inventory"})
        this.resetSearchModelInventory()
        this.updateSearchModelInventory({status, receipt_no: this.receipt.receipt_no})
        this.$router.push({name: "inventory"})
      },
      onSoldItemsClick() {
        this.goToInventoryWithCurrentReceiptAndStatusOf(['sold', 'manually_sold'])
      },
      onUnsoldItemsClick() {
        this.goToInventoryWithCurrentReceiptAndStatusOf(['unsold'])
      },
      onReservedItemsClick() {
        this.goToInventoryWithCurrentReceiptAndStatusOf(['reserved'])
      },
      onTotalItemsClick() {
        this.goToInventoryWithCurrentReceiptAndStatusOf([])
      },

      onRelationEditClick(relationType) {
        let obj = {};

        if (relationType === 'notary' && this.relations.notary) {
          obj = this.relations.notary
        } else if (relationType === 'contact_person' && this.relations.contactPerson) {
          obj = this.relations.contactPerson
        } else if (relationType === 'intermediary' && this.relations.intermediary) {
          obj = this.relations.intermediary
        }

        this.showRelationModal({
          source: SOURCE_RECEIPT_DASHBOARD,
          object: {
            object_uuid: this.receipt.uuid,
            type: relationType,
            ...obj
          },
          relationType,
          title: this.$i18n.t("Receipt Relation")
        })
      },
      async onRelationDeleteClick(relationType = null, obj = null) {

        if (obj === null) {
          if (relationType === 'notary' && this.relations.notary) {
            obj = this.relations.notary
          } else if (relationType === 'contact_person' && this.relations.contactPerson) {
            obj = this.relations.contactPerson
          } else if (relationType === 'intermediary' && this.relations.intermediary) {
            obj = this.relations.intermediary
          }
        }

        if (obj) {

          let success = await this.$confirm(this.$i18n.t("Are you sure you want to remove following relation?"))
          if (!success) {
            return;
          }

          let response = await RelationsService.removeRelation(obj.uuid);
          if (response.success) {
            this.$toast('success', this.$i18n.t("Relation was successfully deleted"));
            this.getDashboardRelations();
          } else {
            if (response.body.non_field_errors) {
              this.$errorDialog(response.body.non_field_errors[0]);
            }
          }
        }
      },
      onAddNewPersonOfInterestClick() {
        let relationType = "person_of_interest";
        this.showRelationModal({
          source: SOURCE_RECEIPT_DASHBOARD,
          object: {
            object_uuid: this.receipt.uuid,
            type: relationType,
          },
          relationType: relationType,
          title: this.$i18n.t("Receipt Relation")
        })
      },
      onEditPersonOfInterestClick(obj) {
        let relationType = "person_of_interest";
        this.showRelationModal({
          source: SOURCE_RECEIPT_DASHBOARD,
          object: {
            object_uuid: this.receipt.uuid,
            type: relationType,
            ...obj
          },
          relationType: relationType,
          title: this.$i18n.t("Receipt Relation")
        })
      },
      getIntermediaryRate(rate) {
        // if (this.inclusiveTiersEnabled && this.currentBranch) {
        //   if (rate) {
        //     return parseFloat(rate * (this.currentBranch.tax_rate / 100 + 1)).toFixed()
        //   }
        // }
        return rate
      },
      onVendorChangeCheckboxChange: _.debounce(async function () {
        let uuids = this.vendorCharges.filter(d => d.checked).map(d => d.uuid)
        let response = await ReceiptService.partialUpdateReceipt(this.receipt.uuid, {
          charges_uuids: uuids.concat(this.hiddenVendorCharges.map(d => d.uuid))
        })
        if (response.success) {
          this.$toast('success', this.$t('Receipt vendor charges were successfully saved'));
        } else {
          if (response.body.non_field_errors) {
            this.$errorDialog(response.body.non_field_errors[0])
          }
        }
      }, 1000),
    },
    async mounted() {
      this.getDashboardData({
        receipt_uuid: this.receipt.uuid
      });
      this.getDashboardRelations()
      const response = await BranchService.getCharges();
      if (response.success) {
        let assignedReceiptCharged = this.receipt.charges ? this.receipt.charges.map(c => c.uuid) : [];
        this.vendorCharges = response.body.results.filter(
          c => c.type === 'vendor' && !c.is_financial_item_based && this.currentBranch.uuid === c.branch.uuid && !c.is_hidden
        ).map(c => {
          return {...c, checked: assignedReceiptCharged.includes(c.uuid)}
        })
        this.hiddenVendorCharges = response.body.results.filter(
          c => c.type === 'vendor' && !c.is_financial_item_based && this.currentBranch.uuid === c.branch.uuid && c.is_hidden
        ).map(c => {
          return {...c, checked: assignedReceiptCharged.includes(c.uuid)}
        })
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import "./../../scss/variables";

  .miscellaneous-table {
    overflow-y: auto;
  }

  .general-info-wrapper {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    grid-gap: 1em;
  }

  .main-wrapper {
    display: grid;
    grid-template-columns: 2fr 2fr 2fr;
    grid-gap: 1em;
    margin-top: 1em;

    .relations-card {
      grid-column: 1/4;
    }

    .miscellaneous-card {
      grid-column: 1/1;

      & .card-body {
        overflow-x: auto;
      }
    }

    .terms-card {
      grid-column: 2/2;
    }

    .charges-card {
      grid-column: 3/3;
    }
  }

  @media only screen and (max-width: map-get($grid-breakpoints, "xl")) {
    .general-info-wrapper {
      grid-template-columns: repeat(3, 2fr);
    }
    .main-wrapper {
      grid-template-columns: 1fr 1fr;

      .relations-card {
        grid-column: 1/4;
      }

      .receipt-card {
        grid-column: 1/4;
      }

      .customer-card {
        grid-column: 1/4;
      }

      .items-by-category-card {
        grid-column: 1/4;
      }

      .terms-card {
        grid-column: 1/4;
      }

      .charges-card {
        grid-column: 1/4;
      }

      .miscellaneous-card {
        grid-column: 1/4;
      }
    }

  }
</style>
